/**
 * Central configuration — single source of truth for environment-dependent constants.
 * Import from here instead of duplicating hostname checks across managers.
 */

// Platform detection lives in a leaf module so AssetSource.js can share it without
// importing this file back (see the assetUrl re-export at the bottom).
export { PLATFORM, IS_NATIVE } from './platform/flags.js';
import { PLATFORM, IS_NATIVE } from './platform/flags.js';

// Build-time flags. A native WebView serves from `localhost`, so the old hostname check
// reported dev in a shipped app; these fold to literals instead, letting Rollup drop the
// guarded code rather than merely skip it. Undefined under vitest, hence the typeof guard.

/** Dev-machine affordances: HUD, country selector, IBL popover, light rig, D/L hotkeys, high-poly. */
export const SHOW_DEV_TOOLS = typeof __DEV_TOOLS__ !== 'undefined' && __DEV_TOOLS__;

/** Unreleased catalog: `__`-prefixed fabric groups and devOnly collections/items. */
export const SHOW_UNRELEASED_CATALOG =
  typeof __SHOW_UNRELEASED__ !== 'undefined' && __SHOW_UNRELEASED__;

/**
 * Public origin that share links point at. Native has no shareable origin of its own —
 * `window.location` is `capacitor://localhost` — so links are built against this instead.
 * Deliberately NOT the host checkout runs on (`feydom.com/app`): claiming that host for
 * deep links would make the app capture its own checkout URL in a bounce loop.
 */
export const CANONICAL_ORIGIN =
  import.meta.env.VITE_CANONICAL_ORIGIN ?? 'https://buildyourownsofa.com';

export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ??
  (SHOW_DEV_TOOLS && !IS_NATIVE ? 'http://localhost:8090' : 'https://feydom.com');

const UA = globalThis.navigator?.userAgent ?? '';

export const IS_MOBILE = IS_NATIVE || /Android|iPhone|iPad|iPod/i.test(UA);

// Apple GPUs take the VSM blur well; `Mac` also catches iPadOS 13+, which reports as a Macintosh and IS_MOBILE misses.
export const IS_APPLE = PLATFORM === 'ios' || /Mac|iPhone|iPad|iPod/i.test(UA);

// Origins allowed to drive a live embed via postMessage (preset / fabric / leg swaps).
export const EMBED_ALLOWED_ORIGINS = [
  // The shop running in docker on the dev box (nginx listens on 8090).
  ...(SHOW_DEV_TOOLS ? ['http://localhost:8090', 'http://127.0.0.1:8090'] : []),
  'https://threejs-designer.feydom.com',
  'https://makeyourownsofa.com',
  'https://buildyourownsofa.com',
  'https://feydom.al',
  'https://feydom.at',
  'https://feydom.ba',
  'https://feydomliving.be',
  'https://feydom.bg',
  'https://feydom.ca',
  'https://feydom.ch',
  'https://feydom.com.cn',
  'https://feydom.com',
  'https://feydom.cz',
  // 'https://feydom.de',
  'https://feydomliving.dk',
  'https://feydom.ee',
  'https://feydom.es',
  'https://feydom.fi',
  'https://feydomliving.fr',
  'https://feydom.uk',
  'https://feydom.gr',
  // 'https://feydom.hr',
  'https://feydom.hu',
  'https://feydom.ie',
  // 'https://feydom.it',
  'https://feydom.lt',
  'https://feydom.lv',
  'https://feydom.mk',
  'https://feydomliving.nl',
  'https://feydomliving.pl',
  'https://feydom.pt',
  'https://feydom.ro',
  'https://feydom.rs',
  'https://feydom.se',
  // 'https://feydom.si',
  'https://feydomliving.sk',
  'https://feydom-usa.com'

];

// Asset resolution lives in AssetSource.js so the native build can stream and cache the
// catalog; re-exported here so every existing `import { assetUrl } from config.js` is
// untouched. The dependency is one-way — AssetSource must not import this file.
export { BUILD_ID, assetUrl, ASSET_ORIGIN } from './core/AssetSource.js';
